// src/utils/WebSocketManager.js
import { handleSessionEnded } from "./impersonation";

class WebSocketManager {
  constructor(token) {
    this.token = token;
    this.ws = null;
    this.subscribers = {};
    this.queue = [];
    this.status = "disconnected";
    this.pingInterval = null;
    this.pongTimeout = null;
    this.lastSubscriptions = [];
    this._intentionalClose = false;
    if (token) {
      this.connect();
    }
  }

  connect() {
    this.status = "connecting";
    this.ws = new WebSocket(`${process.env.REACT_APP_API_WS_URI}?token=${this.token}`);
    this.ws.onopen = () => {
      this.status = "connected";
      this.queue.forEach(msg => this.ws.send(msg));
      this.queue = [];
      this.resubscribe();
      this.startPing();
    };
    this.ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);
      if (msg.type === "pong") {
        this.clearPongTimeout();
      }
      if (msg.type === "candleUpdate" && msg.symbol) {
        this.notify(msg.symbol, msg);
        // The forming candle's close is the market's latest price. Whatever a
        // chart already streams doubles as a live mark (useMarkPrices).
        this.notify("candleMarks", msg);
      }
      if (msg.type === "bookUpdate" && msg.symbol && msg.exchangeId) {
        this.notify(`book:${msg.exchangeId}:${msg.symbol}`, msg);
      }
      if (msg.type === "botLogUpdate" && msg.botType && msg.botId) {
        const subscriberKey = `botlog:${msg.botType}-${msg.botId}`;
        this.notify(subscriberKey, msg);
      }
      if (msg.type === "accountUpdate" && msg.accountId) {
        const subscriberKey = `account:${msg.accountId}`;
        this.notify(subscriberKey, msg);
      }
      if (msg.type === "walletUpdate" && msg.accountId) {
        const subscriberKey = `account:${msg.accountId}`;
        this.notify(subscriberKey, msg);
      }
      if (msg.type === "orderUpdate" || msg.type === "ordersUpdate" || msg.type === "orders" ||
          msg.type === "ordersDone" || msg.type === "ordersError") {
        this.notify("orders", msg);
      }
      // `markUpdate` is the bare price tick for a symbol the user holds a bot
      // deal on but no terminal position (useMarkPrices reads it).
      if (msg.type === "positionsUpdate" || msg.type === "positionClosed" || msg.type === "positionPnlUpdate"
          || msg.type === "markUpdate") {
        this.notify("positions", msg);
      }
      // Route VOrder lifecycle events to the "vorders" channel
      // (server publishes vorder:created, vorder:updated, vorder:triggered,
      //  vorder:filled, vorder:cancelled, vorder:failed)
      if (typeof msg.type === "string" && msg.type.startsWith("vorder:")) {
        this.notify("vorders", msg);
      }
      // Route the bots v3 deal stream to the "deals" channel (server publishes
      // deal:created, deal:updated, deal:closed, bot:updated)
      if (typeof msg.type === "string" && (msg.type.startsWith("deal:") || msg.type.startsWith("bot:"))) {
        this.notify("deals", msg);
      }
      // Route trade fill events to the "trades" channel
      if (msg.type === "tradeUpdate" || msg.type === "tradesUpdate") {
        this.notify("trades", msg);
      }
      // Route indicator-monitor / event subscription updates to the "events" channel
      if (msg.type === "eventUpdate" || msg.type === "eventsUpdate") {
        this.notify("events", msg);
      }
      // Route order error push notifications.
      // Risk-limit notifications share the orderErrors channel; they carry a
      // human-readable `message` or `reason` field instead of `error`.
      if (msg.type === "orderError" || msg.type === "riskLimitBreached" || msg.type === "botPausedRiskLimit") {
        this.notify("orderErrors", msg);
      }
      // Route order success push notifications
      if (msg.type === "orderSuccess") {
        this.notify("orderResults", msg);
      }
    };
    this.ws.onclose = (event) => {
      this.status = "disconnected";
      this.stopPing();
      // Don't reconnect if disconnect() was called (intentional logout)
      if (this._intentionalClose) return;
      if (
        (event.code === 4001 || (event.reason && event.reason.includes("token"))) &&
        !window.location.pathname.startsWith("/login")
      ) {
        // An expired impersonation goes back to the admin's own login
        if (handleSessionEnded()) return;
        window.location.href = "/login";
        return;
      }
      // Verify token still exists before reconnecting — prevents reconnect
      // attempts with revoked/expired tokens after logout on another device.
      setTimeout(() => {
        const currentToken = localStorage.getItem('token');
        if (!currentToken) {
          this.status = "disconnected";
          return;
        }
        this.token = currentToken;
        this.connect();
      }, 1000);
    };
    this.ws.onerror = () => {
      this.status = "disconnected";
      this.stopPing();
      this.ws.close();
    };
  }

  // Cleanly close the WebSocket and prevent automatic reconnection.
  // Used on logout to stop all real-time data streaming.
  disconnect() {
    this._intentionalClose = true;
    this.stopPing();
    this.subscribers = {};
    this.queue = [];
    this.lastSubscriptions = [];
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.status = "disconnected";
  }

  startPing() {
    this.stopPing();
    this.pingInterval = setInterval(() => {
      if (this.ws.readyState === 1) {
        this.ws.send(JSON.stringify({ type: "ping" }));
        this.setPongTimeout();
      }
    }, 10000); // alle 10 Sekunden
  }

  stopPing() {
    if (this.pingInterval) clearInterval(this.pingInterval);
    this.clearPongTimeout();
  }

  setPongTimeout() {
    this.clearPongTimeout();
    this.pongTimeout = setTimeout(() => {
      this.ws.close(); // Verbindung neu aufbauen
    }, 5000); // 5 Sekunden auf Pong warten
  }

  clearPongTimeout() {
    if (this.pongTimeout) clearTimeout(this.pongTimeout);
  }

  resubscribe() {
    this.lastSubscriptions.forEach(msg => {
      this.ws.send(msg);
    });
  }

  getStatus() {
    return this.status;
  }

  /** `accountId` streams one account's candles, for venues whose quotes differ per account (MT5). */
  subscribeCandles(exchangeId, symbol, callback, accountId) {
    if (!this.subscribers[symbol]) this.subscribers[symbol] = [];
    this.subscribers[symbol].push(callback);
    const message = JSON.stringify({
      type: "subscribeCandles",
      exchangeId,
      symbol,
      ...(accountId ? { accountId } : {}),
    });
    this.lastSubscriptions.push(message);
    if (this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  unsubscribeCandle(exchangeId, symbol, callback, accountId) {
    if (this.subscribers[symbol]) {
      this.subscribers[symbol] = this.subscribers[symbol].filter(cb => cb !== callback);
      if (this.subscribers[symbol].length === 0) {
        delete this.subscribers[symbol];
        const message = JSON.stringify({
          type: "unsubscribeCandles",
          exchangeId,
          symbol,
          ...(accountId ? { accountId } : {}),
        });
        // Match on the type as well: the order book subscription for the same
        // market also carries a "symbol" field, and dropping it here would take
        // the book off the reconnect replay list without anyone noticing.
        this.lastSubscriptions = this.lastSubscriptions.filter(
          msg => !(msg.includes('"type":"subscribeCandles"') && msg.includes(`"symbol":"${symbol}"`))
        );
        if (this.ws.readyState === 1) {
          this.ws.send(message);
        } else {
          this.queue.push(message);
        }
      }
    }
  }

  /**
   * Subscribe to live order book snapshots for one market.
   * Receives messages of type `bookUpdate` carrying
   * `{ ts, bids: [[price, amount], ...], asks: [...] }` with decimal strings.
   * The subscription is queued while the socket is opening and replayed on reconnect.
   * @param {string} exchangeId
   * @param {string} symbol
   * @param {function(object): void} callback - Called with each incoming book message.
   */
  subscribeOrderBook(exchangeId, symbol, callback) {
    const channel = `book:${exchangeId}:${symbol}`;
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    const first = this.subscribers[channel].length === 0;
    this.subscribers[channel].push(callback);
    // The server subscription is per connection and per market — a second send
    // would only come back as "already subscribed".
    if (!first) return;
    const message = JSON.stringify({ type: "subscribeOrderBook", exchangeId, symbol });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  /**
   * Unsubscribe a callback from order book updates.
   * Sends `unsubscribeOrderBook` once the last local subscriber is gone.
   * @param {string} exchangeId
   * @param {string} symbol
   * @param {function(object): void} callback - The same reference passed to subscribeOrderBook.
   */
  unsubscribeOrderBook(exchangeId, symbol, callback) {
    const channel = `book:${exchangeId}:${symbol}`;
    if (!this.subscribers[channel]) return;
    this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
    if (this.subscribers[channel].length > 0) return;

    delete this.subscribers[channel];
    const message = JSON.stringify({ type: "unsubscribeOrderBook", exchangeId, symbol });
    this.lastSubscriptions = this.lastSubscriptions.filter(
      msg => !(msg.includes('"type":"subscribeOrderBook"') && msg.includes(`"symbol":"${symbol}"`))
    );
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    }
  }

  subscribeBotLog(botType, botId, callback) {
    const symbol = `botlog:${botType}-${botId}`;
    if (!this.subscribers[symbol]) this.subscribers[symbol] = [];
    this.subscribers[symbol].push(callback);
    const message = JSON.stringify({
      type: "subscribeBotLog",
      botType,
      botId
    });
    this.lastSubscriptions.push(message);
    if (this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  unsubscribeBotLog(botType, botId, callback) {
    const symbol = `botlog:${botType}-${botId}`;
    if (this.subscribers[symbol]) {
      this.subscribers[symbol] = this.subscribers[symbol].filter(cb => cb !== callback);
      if (this.subscribers[symbol].length === 0) {
        delete this.subscribers[symbol];
        const message = JSON.stringify({
          type: "unsubscribeBotLog",
          botType,
          botId
        });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes(`"botType":"${botType}"`) || !msg.includes(`"botId":"${botId}"`));
        if (this.ws.readyState === 1) {
          this.ws.send(message);
        } else {
          this.queue.push(message);
        }
      }
    }
  }

  subscribeAccount(accountId, callback) {
    const channel = `account:${accountId}`;
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
    const message = JSON.stringify({
      type: "subscribeAccount",
      accountId
    });
    this.lastSubscriptions.push(message);
    if (this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  unsubscribeAccount(accountId, callback) {
    const channel = `account:${accountId}`;
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({
          type: "unsubscribeAccount",
          accountId
        });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes(`"accountId":"${accountId}"`));
        if (this.ws.readyState === 1) {
          this.ws.send(message);
        } else {
          this.queue.push(message);
        }
      }
    }
  }

  subscribeOrders(callback) {
    const channel = "orders";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    const first = this.subscribers[channel].length === 0;
    this.subscribers[channel].push(callback);
    // The server subscription is per connection — only the first local
    // subscriber needs to send it (additional sends would just produce
    // "already subscribed" errors and duplicate reconnect replays).
    if (!first) return;
    const message = JSON.stringify({
      type: "subscribeOrders"
    });
    this.lastSubscriptions.push(message);
    if (this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  unsubscribeOrders(callback) {
    const channel = "orders";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({
          type: "unsubscribeOrders"
        });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribeOrders"'));
        if (this.ws.readyState === 1) {
          this.ws.send(message);
        } else {
          this.queue.push(message);
        }
      }
    }
  }

  subscribePositions(callback) {
    const channel = "positions";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    const first = this.subscribers[channel].length === 0;
    this.subscribers[channel].push(callback);
    // Per-connection server subscription — see subscribeOrders.
    if (!first) return;
    const message = JSON.stringify({ type: "subscribePositions" });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  unsubscribePositions(callback) {
    const channel = "positions";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({ type: "unsubscribePositions" });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribePositions"'));
        if (this.ws && this.ws.readyState === 1) {
          this.ws.send(message);
        }
      }
    }
  }

  /**
   * Subscribe to virtual order (VOrder) updates from the server.
   * Receives messages of type: `vorder:created`, `vorder:updated`,
   * `vorder:triggered`, `vorder:filled`, `vorder:cancelled`, `vorder:failed`.
   * The subscription is queued if the WebSocket is not yet open and replayed on reconnect.
   * @param {function(object): void} callback - Called with each incoming VOrder message.
   */
  subscribeVOrders(callback) {
    const channel = "vorders";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
    const message = JSON.stringify({ type: "subscribeVOrders" });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  /**
   * Unsubscribe a callback from VOrder updates.
   * Sends `unsubscribeVOrders` to the server when the last subscriber is removed.
   * @param {function(object): void} callback - The same reference passed to subscribeVOrders.
   */
  unsubscribeVOrders(callback) {
    const channel = "vorders";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({ type: "unsubscribeVOrders" });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribeVOrders"'));
        if (this.ws && this.ws.readyState === 1) {
          this.ws.send(message);
        }
      }
    }
  }

  /**
   * Subscribe to the bots v3 deal stream.
   * Receives messages of type: `deal:created`, `deal:updated`, `deal:closed`,
   * `bot:updated` — each naming the deal and the bot it is about. The pages
   * reload their REST payload on it; the message carries no deal data itself.
   * Per-connection server subscription — see subscribeOrders.
   * @param {function(object): void} callback - Called with each incoming deal message.
   */
  subscribeDeals(callback) {
    const channel = "deals";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    const first = this.subscribers[channel].length === 0;
    this.subscribers[channel].push(callback);
    if (!first) return;
    const message = JSON.stringify({ type: "subscribeDeals" });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  /**
   * Unsubscribe a callback from the deal stream.
   * Sends `unsubscribeDeals` to the server when the last subscriber is removed.
   * @param {function(object): void} callback - The same reference passed to subscribeDeals.
   */
  unsubscribeDeals(callback) {
    const channel = "deals";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({ type: "unsubscribeDeals" });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribeDeals"'));
        if (this.ws && this.ws.readyState === 1) {
          this.ws.send(message);
        }
      }
    }
  }

  /**
   * Subscribe to trade history updates from the server.
   * Receives messages of type: `tradeUpdate`, `tradesUpdate`.
   * @param {function(object): void} callback - Called with each incoming trade message.
   */
  subscribeTrades(callback) {
    const channel = "trades";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
    const message = JSON.stringify({ type: "subscribeTrades" });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  /**
   * Unsubscribe a callback from trade updates.
   * Sends `unsubscribeTrades` to the server when the last subscriber is removed.
   * @param {function(object): void} callback - The same reference passed to subscribeTrades.
   */
  unsubscribeTrades(callback) {
    const channel = "trades";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({ type: "unsubscribeTrades" });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribeTrades"'));
        if (this.ws && this.ws.readyState === 1) {
          this.ws.send(message);
        }
      }
    }
  }

  /**
   * Subscribe to event subscription updates from the server.
   * Receives messages of type: `eventUpdate`, `eventsUpdate`.
   * Events represent active indicator monitors (e.g. "RSI > 70") that trigger VOrders.
   * @param {function(object): void} callback - Called with each incoming event message.
   */
  subscribeEvents(callback) {
    const channel = "events";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
    const message = JSON.stringify({ type: "subscribeEvents" });
    this.lastSubscriptions.push(message);
    if (this.ws && this.ws.readyState === 1) {
      this.ws.send(message);
    } else {
      this.queue.push(message);
    }
  }

  /**
   * Unsubscribe a callback from event updates.
   * Sends `unsubscribeEvents` to the server when the last subscriber is removed.
   * @param {function(object): void} callback - The same reference passed to subscribeEvents.
   */
  unsubscribeEvents(callback) {
    const channel = "events";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
        const message = JSON.stringify({ type: "unsubscribeEvents" });
        this.lastSubscriptions = this.lastSubscriptions.filter(msg => !msg.includes('"type":"subscribeEvents"'));
        if (this.ws && this.ws.readyState === 1) {
          this.ws.send(message);
        }
      }
    }
  }

  /**
   * Subscribe to order-error push notifications (local only).
   * The server auto-subscribes this channel when orders are subscribed,
   * so no WS message is sent here.
   */
  subscribeOrderErrors(callback) {
    const channel = "orderErrors";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
  }

  unsubscribeOrderErrors(callback) {
    const channel = "orderErrors";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
      }
    }
  }

  /**
   * Subscribe to order-success push notifications (local only).
   * Uses the same server-side auto-subscription as order errors.
   */
  subscribeOrderResults(callback) {
    const channel = "orderResults";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
  }

  unsubscribeOrderResults(callback) {
    const channel = "orderResults";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
      }
    }
  }

  /**
   * Listen to every `candleUpdate` this connection receives, whatever symbol
   * (local only). Opens no server subscription: it hears the candles a chart
   * on the page already subscribed to.
   */
  subscribeCandleMarks(callback) {
    const channel = "candleMarks";
    if (!this.subscribers[channel]) this.subscribers[channel] = [];
    this.subscribers[channel].push(callback);
  }

  unsubscribeCandleMarks(callback) {
    const channel = "candleMarks";
    if (this.subscribers[channel]) {
      this.subscribers[channel] = this.subscribers[channel].filter(cb => cb !== callback);
      if (this.subscribers[channel].length === 0) {
        delete this.subscribers[channel];
      }
    }
  }

  notify(symbol, data) {
    // console.log('Received msg: ', symbol, data);
    if (this.subscribers[symbol]) {
      this.subscribers[symbol].forEach(cb => cb(data));
    }
  }
}

export default WebSocketManager;